import React from 'react'
import { useRouteMatch } from 'react-router-dom'

import { useBreakpoint, Dropdown } from '@sketch/components'
import { useFlag } from 'containers/flags'

import { useVersioning } from 'modules/versioning'

import { IS_EMBEDDED } from '@sketch/constants'
import {
  isCwvRouteOnly,
  isDocumentRoute,
  isDocumentPageRoute,
} from 'utils/route'

import {
  DropdownItem,
  ViewWrapper,
  PrototypeIcon,
  StyledSeparator,
  ComponentsIcon,
  PageIcon,
} from '../HeaderNavigation/NavigationItem.styles'

import {
  COMPONENTS_TEXT,
  PAGES_TEXT,
  PROTOTYPES_TEXT,
} from '../HeaderNavigation/PagesNavigationItem/constants'
import PageList from '../PageList'
import { useGetPagesList } from '../../hooks/useGetPagesList'
import { useIsCanvasFallback } from '../../hooks/useIsCanvasFallback'

interface ViewsSelectorProps {
  shareIdentifier: string
  isMobile: boolean
  isCanvasFallback: boolean
}

const ViewsSelector: React.FC<ViewsSelectorProps> = ({
  shareIdentifier,
  isMobile,
  isCanvasFallback,
}) => {
  const includeHasUnreadComments = useFlag('pages-unread-annotations')
  const { path } = useRouteMatch()

  const { getPathname, versionShortId } = useVersioning()!
  const { componentCount, pagesCount, prototypesCount } = useGetPagesList({
    shareIdentifier,
    versionShortId,
    includeHasUnreadComments,
  })

  const componentsCountTotal =
    (componentCount?.symbol || 0) +
    (componentCount?.textStyle || 0) +
    (componentCount?.layerStyle || 0) +
    (componentCount?.colorVar || 0)

  return (
    <ViewWrapper>
      {isMobile && <StyledSeparator />}
      {!isMobile && <Dropdown.Header>Views</Dropdown.Header>}
      {!isMobile && (
        <DropdownItem
          key={PAGES_TEXT}
          to={getPathname({
            routeKey: isCanvasFallback ? 'SHARE_VIEW_GRID' : 'SHARE_VIEW',
            routeParams: { shareID: shareIdentifier },
          })}
          isActive={() => isDocumentRoute(path) || isDocumentPageRoute(path)}
        >
          <PageIcon />
          {`${PAGES_TEXT} (${pagesCount || 0})`}
        </DropdownItem>
      )}
      <DropdownItem
        to={getPathname({
          routeKey: 'CWV_SYMBOLS',
          routeParams: { shareID: shareIdentifier },
        })}
        isActive={() => isCwvRouteOnly(path)}
      >
        <ComponentsIcon />
        {`${COMPONENTS_TEXT} (${componentsCountTotal})`}
      </DropdownItem>
      <DropdownItem
        to={getPathname({
          routeKey: 'SHARE_PROTOTYPES',
          routeParams: { shareID: shareIdentifier },
        })}
      >
        <PrototypeIcon />
        {`${PROTOTYPES_TEXT} (${prototypesCount || 0})`}
      </DropdownItem>
    </ViewWrapper>
  )
}

interface PagesDropdownProps {
  shareIdentifier: string
  artboardPageUUID?: string
}

export const PagesDropdown: React.FC<PagesDropdownProps> = props => {
  const { shareIdentifier } = props

  const isMobile = !useBreakpoint('xs')
  const isCanvasFallback = useIsCanvasFallback()

  const showPagesList = isMobile || IS_EMBEDDED

  return (
    <>
      {showPagesList && (
        <div>
          <Dropdown.Header>{PAGES_TEXT}</Dropdown.Header>
          <PageList isDropdown />
        </div>
      )}

      {!IS_EMBEDDED && (
        <ViewsSelector
          shareIdentifier={shareIdentifier}
          isMobile={isMobile}
          isCanvasFallback={isCanvasFallback}
        />
      )}
    </>
  )
}
